/**
 * Common response types used across multiple API endpoints
 */

import type { RpaModuleName } from './backend_config.types';
import type { LosslessNumber } from './lossless-json-codec';

// Traceability: implements REQ-LEGACY-APPROVED.
// HTTP containment codes are distinct from payment business outcomes.
export const HttpErrorCode = Object.freeze({
    INVALID_INPUT: 'INVALID_INPUT',
    AUTHENTICATION_REQUIRED: 'AUTHENTICATION_REQUIRED',
    SESSION_INVALID: 'SESSION_INVALID',
    FORBIDDEN: 'FORBIDDEN',
    RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
    METHOD_NOT_ALLOWED: 'METHOD_NOT_ALLOWED',
    STATE_CONFLICT: 'STATE_CONFLICT',
    RESOURCE_UNAVAILABLE: 'RESOURCE_UNAVAILABLE',
    INPUT_TOO_LARGE: 'INPUT_TOO_LARGE',
    UNSUPPORTED_MEDIA_TYPE: 'UNSUPPORTED_MEDIA_TYPE',
    RATE_LIMITED: 'RATE_LIMITED',
    REQUEST_REJECTED: 'REQUEST_REJECTED',
    TOKEN_INVALID: 'TOKEN_INVALID',
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    TOKEN_CONSUMED: 'TOKEN_CONSUMED',
    TOKEN_UNAVAILABLE: 'TOKEN_UNAVAILABLE',
    FEATURE_UNAVAILABLE: 'FEATURE_UNAVAILABLE',
    DEPENDENCY_UNAVAILABLE: 'DEPENDENCY_UNAVAILABLE',
    DEPENDENCY_OUTCOME_UNKNOWN: 'DEPENDENCY_OUTCOME_UNKNOWN',
    OPERATION_OUTCOME_UNKNOWN: 'OPERATION_OUTCOME_UNKNOWN',
});
export type HttpErrorCode = (typeof HttpErrorCode)[keyof typeof HttpErrorCode];

export type JsonPrimitive = string | number | LosslessNumber | boolean | null;

export interface JsonObject {
    [key: string]: JsonValue | undefined;
}

// Use an interface for the array to allow for circular reference within type alias
export interface JsonArray extends Array<JsonValue> {}

export type JsonValue = JsonPrimitive | JsonObject | JsonArray;

/**
 * Generic success message response
 */
export interface MessageResponse {
    message: string;
}

/**
 * Health check response
 */
export interface HealthResponse {
    status: 'ok' | 'unhealthy';
    timestamp: string; // ISO date string
    database: {
        connected: boolean;
    };
}

export interface CspViolationReportBody {
    'document-uri'?: string;
    referrer?: string;
    'violated-directive'?: string;
    'effective-directive'?: string;
    'original-policy'?: string;
    'blocked-uri'?: string;
    'status-code'?: number;
    'source-file'?: string;
    'line-number'?: number;
    'column-number'?: number;
}

export interface CspViolationReport {
    'csp-report'?: CspViolationReportBody;
    [key: string]: unknown;
}

export interface CspReportingApiReportBody {
    blockedURL?: string;
    columnNumber?: number;
    disposition?: string;
    documentURL?: string;
    effectiveDirective?: string;
    lineNumber?: number;
    originalPolicy?: string;
    referrer?: string;
    sample?: string;
    sourceFile?: string;
    statusCode?: number;
}

export interface CspReportingApiReport {
    age?: number;
    body?: CspReportingApiReportBody;
    type?: string;
    url?: string;
    user_agent?: string;
    [key: string]: unknown;
}

export type CspViolationReportPayload = CspViolationReport | ReadonlyArray<CspReportingApiReport>;

/**
 * Samba module version response
 */
export interface SambaVersionResponse {
    version: string;
}

export interface SambaModuleDescriptor {
    name: RpaModuleName;
    version: string;
}

export type SambaModuleListResponse = SambaModuleDescriptor[];

/**
 * Samba update release query (client -> portal)
 */
export type SambaPlatform = 'mac' | 'win';
export type SambaArch = 'x64' | 'arm64';
export type SambaTrack = 'stable' | 'latest';
export type SambaPipeline = 'stable';
export type SambaPackageType = 'portable' | 'installer';

export type SambaLatestReleaseQuery =
    | { platform: 'mac'; arch: SambaArch }
    | { platform: 'win'; arch: SambaArch };

export interface SambaInstallerQaQuery {
    track: SambaTrack;
    arch: SambaArch;
}

export interface SambaWindowsPackageReleaseV1 {
    arch: SambaArch;
    packageType: SambaPackageType;
    downloadUrl: string;
    sha256: string;
}

export interface SambaWindowsPackageRelease extends SambaWindowsPackageReleaseV1 {
    publicFileName: string;
}

export interface SambaWindowsTrackReleaseV1 {
    track: SambaTrack;
    pipeline: SambaPipeline;
    imageLabel: string;
    displayLabel: string;
    packages: SambaWindowsPackageReleaseV1[];
}

export interface SambaWindowsTrackRelease extends SambaWindowsTrackReleaseV1 {
    packages: SambaWindowsPackageRelease[];
}

/** Build-507 and earlier wire representation retained for native-client compatibility. */
export interface SambaLatestReleaseResponseV1 {
    releaseVersion: string;
    buildNumber: string;
    downloadUrl: string;
    sha256: string;
    releaseNotesUrl?: string;
    minSupportedVersion?: string;
    windowsTracks?: SambaWindowsTrackReleaseV1[];
}

/**
 * Samba latest release response (portal -> client)
 */
export interface SambaLatestReleaseResponse extends SambaLatestReleaseResponseV1 {
    identity: import('./native-release-catalog.types').ReleaseIdentityV1;
    artifactTag: string;
    publicFileName: string;
    windowsTracks?: SambaWindowsTrackRelease[];
}
